// The limits on a picture file (38-desyr-id-picture-key.md, Behaviour 1.10; 21-user-settings.md, rule 1;
// v1.58.63): a JPEG, PNG or WebP told by its first bytes (never SVG, GIF, HEIC or anything else), at most
// 5 MiB and 4096 × 4096 pixels, decoded by the browser before anything is uploaded. desyr's own uploads
// are scaled beforehand to 1024 pixels on the longer side, so they always pass.

export const MAX_BYTES = 5 * 1024 * 1024;
const MAX_SIDE = 4096;
const UPLOAD_SIDE = 1024;
export type PictureType = 'image/jpeg' | 'image/png' | 'image/webp';
/** What the file picker asks for (21, rule 1). */
export const PICTURE_TYPES = 'image/jpeg,image/png,image/webp';

const starts = (bytes: Uint8Array, at: number, sign: number[]) => sign.every((b, i) => bytes[at + i] === b);
const ascii = (text: string) => [...text].map((c) => c.charCodeAt(0));
/** The picture's type by its first bytes, never by its name or the type it claims. */
export function typeOf(bytes: Uint8Array): PictureType | undefined {
  if (starts(bytes, 0, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (starts(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (starts(bytes, 0, ascii('RIFF')) && starts(bytes, 8, ascii('WEBP'))) return 'image/webp';
  return undefined;
}

/** A body read as a stream and cut off past `max` bytes, whatever its `Content-Length` says; undefined then. */
export async function readLimited(answer: Response, max = MAX_BYTES): Promise<Uint8Array<ArrayBuffer> | undefined> {
  if (!answer.body) return undefined;
  const reader = answer.body.getReader();
  const parts: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    size += value.byteLength;
    if (size > max) return void (await reader.cancel().catch(() => {}));
    parts.push(value);
  }
  const all = new Uint8Array(size);
  let at = 0;
  for (const p of parts) (all.set(p, at), (at += p.byteLength));
  return all;
}

/** The decoded size, or undefined when the browser can't decode it. */
async function sizeOf(file: Blob): Promise<{ width: number; height: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return undefined;
  }
}

/** A file desyr may copy into a circle (1.10): typed by its bytes, within the limits; else undefined. */
export async function withinLimits(bytes: Uint8Array<ArrayBuffer>): Promise<Blob | undefined> {
  const type = typeOf(bytes);
  if (!type || bytes.byteLength > MAX_BYTES) return undefined;
  const file = new Blob([bytes], { type });
  const size = await sizeOf(file);
  return size && size.width <= MAX_SIDE && size.height <= MAX_SIDE ? file : undefined;
}

/**
 * Upload (21, rule 1): a JPEG, PNG or WebP the browser can decode; one larger than 1024 pixels on a side
 * or than 5 MiB is first scaled to 1024 on its longer side (JPEG at quality 0.9, or PNG when it has
 * transparent parts). Undefined: not a picture ("Please choose a picture …").
 */
export async function forUpload(file: Blob): Promise<Blob | undefined> {
  const type = typeOf(new Uint8Array(await file.slice(0, 16).arrayBuffer()));
  if (!type) return undefined;
  const bitmap = await createImageBitmap(file).catch(() => undefined);
  if (!bitmap) return undefined;
  const { width, height } = bitmap;
  if (width <= UPLOAD_SIDE && height <= UPLOAD_SIDE && file.size <= MAX_BYTES) return (bitmap.close(), new Blob([file], { type }));
  const scale = UPLOAD_SIDE / Math.max(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * Math.min(1, scale)));
  canvas.height = Math.max(1, Math.round(height * Math.min(1, scale)));
  const context = canvas.getContext('2d');
  if (!context) return (bitmap.close(), undefined);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const png = type !== 'image/jpeg' && transparent(context.getImageData(0, 0, canvas.width, canvas.height).data);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b ?? undefined), png ? 'image/png' : 'image/jpeg', 0.9));
}

/** Any pixel not fully opaque. */
function transparent(pixels: Uint8ClampedArray): boolean {
  for (let i = 3; i < pixels.length; i += 4) if (pixels[i] < 255) return true;
  return false;
}
